<template>
  <div
    :class="[
      { '--feed': isFeed, '--comments': isComment, '--is-self': isSelf },
      'p-relative reply-thread'
    ]"
  >
    <template
      v-if="
        !message.reply_thread_id ||
          (isPresent(messageThread) && messageThread.message_count === 0)
      "
    >
      <v-link
        :to="isPresent(messageThread) ? replyThreadUrl : newThreadUrl"
        class="d-flex"
      >
        <v-icon v-if="isFeed" icon="forum" class="reply-thread__icon ml-1" />
        <small
          class="ml-1 color-brand font-weight-semi-bold d-flex align-items-center"
        >
          {{ $t("components.messageThread.replyThread.reply") }}
        </small>
      </v-link>
    </template>
    <template v-else-if="isLoading">
      <div class="d-flex align-items-center loader">
        <placeholder-rect height="32px" width="32px" class="m-0" />
        <placeholder-rect height="18px" width="60px" class="m-0 mx-1" />
      </div>
    </template>
    <template v-else-if="messageThread">
      <v-link :to="replyThreadUrl" class="d-flex align-items-center">
        <subscriber-avatars
          :subscribers="participants.map(p => p.user)"
          x-small
        />
        <small class="ml-1 color-brand font-weight-semi-bold">
          <transition name="fade" mode="out-in">
            <span :key="messageThread.message_count">
              {{ messageThread.message_count }}&nbsp;{{ pluralReply }}
            </span>
          </transition>
        </small>

        <transition name="fade" mode="out-in">
          <small
            v-if="latestMessage"
            :key="latestMessage.message_type + latestMessage.message_ts"
            class="reply-thread__message text-grey-60 ml-1"
          >
            {{
              latestMessage.message_type === MESSAGE_TYPES.VIDEO
                ? $t("components.messageThread.replyThread.sentVideo")
                : latestMessage.message_type === MESSAGE_TYPES.IMAGE
                ? $t("components.messageThread.replyThread.sentImage")
                : latestMessage.message_type === MESSAGE_TYPES.ATTACHMENT
                ? latestMessage.attachment_data.url
                : latestMessage.message
            }}
          </small>
        </transition>
      </v-link>
    </template>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex"

import { MESSAGE_TYPES } from "@/lib/pusher-utils"
import { isPresent } from "@/lib/utils"
import { buildMessageId } from "@/lib/messages-utils"

import PlaceholderRect from "@/components/PlaceholderRect"

export default {
  components: {
    PlaceholderRect,
    SubscriberAvatars: () => import("@/components/SubscriberAvatars")
  },

  props: {
    message: { type: Object, required: true },
    isFeed: { type: Boolean, default: false },
    isComment: { type: Boolean, default: false },
    isSelf: { type: Boolean, default: false }
  },

  data() {
    return {
      replyThreadSubscription: null,
      isLoading: false,
      MESSAGE_TYPES
    }
  },

  computed: {
    ...mapGetters("messages", ["getMessageThread"]),
    ...mapGetters("auth", ["isAuthenticated"]),

    replyThreadId() {
      return this.message.reply_thread_id
    },

    messageThread() {
      return this.getMessageThread(this.replyThreadId)
    },

    latestMessage() {
      return this.messageThread?.latest_message
    },

    participants() {
      return this.messageThread?.participants || []
    },

    pluralReply() {
      return this.messageThread.message_count > 1
        ? this.$t("components.messageThread.replyThread.replies")
        : this.$t("components.messageThread.replyThread.reply")
    },

    threadSubscriptionParams() {
      return {
        subscriptionId: `reply-thread-${this.replyThreadId}`,
        messageThreadId: this.replyThreadId
      }
    },

    replyThreadUrl() {
      return `/me/messages/${this.replyThreadId}?reply=true`
    },

    newThreadUrl() {
      return `/me/messages/new?message=${buildMessageId(this.message)}`
    }
  },

  methods: {
    isPresent,

    ...mapActions("messages", [
      "fetchMessageThread",
      "subscribeToThread",
      "unsubscribeFromThread"
    ]),
    ...mapMutations("messages", ["setMessageThread"]),

    loadThread() {
      if (this.messageThread) {
        this.subscribeToThread(this.threadSubscriptionParams)
      } else if (this.replyThreadId) {
        this.isLoading = !this.messageThread
        this.fetchMessageThread(this.replyThreadId)
          .then(() => {
            if (this.isAuthenticated) {
              this.subscribeToThread(this.threadSubscriptionParams)
            }
          })
          .finally(() => {
            this.isLoading = false
          })
      }
    },

    teardownThread() {
      this.unsubscribeFromThread(this.threadSubscriptionParams)
    }
  },

  beforeDestroy() {
    this.teardownThread()
  },

  deactivated() {
    this.teardownThread()
  },

  activated() {
    this.loadThread()
  },

  mounted() {
    this.loadThread()
  },

  watch: {
    replyThreadId() {
      if (this.replyThreadId) {
        this.loadThread()
      }
    },

    isAuthenticated() {
      if (this.isAuthenticated) {
        this.loadThread()
        this.fetchMessageThread(this.replyThreadId) // This ensures the currentParticipant is updated
      }
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";
@import "@/assets/styles/mixins";

.reply-thread {
  margin-bottom: 0.5rem;
  margin-left: 2.3rem;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 6px;

  .reply-thread__message {
    @include overflow-ellipsis;
  }

  &.--feed {
    padding-top: $size--4;
    margin-left: 0;
    flex: 1;
    min-width: 0;

    &.--is-self {
      .v-link {
        flex-direction: row;
      }
      .subscriber-avatars {
        margin-left: 0;
      }
    }
  }

  &:not(.--feed):not(.--comments).--is-self {
    margin-right: 2.3rem;
    margin-left: 0;

    .v-link,
    .loader {
      flex-direction: row-reverse;
    }

    .subscriber-avatars {
      margin-left: $size--2;
    }
  }
}

.reply-thread__icon {
  font-size: $font-size--large;
}
</style>
